import { Timestamp } from 'firebase/firestore';

// ShareBoost Analytics Types

export interface CashbackTransaction {
  id: string;
  type: 'earned' | 'paid' | 'cancelled' | 'refunded';
  sharerId: string;
  sharerEmail: string;
  sharerName: string;
  eventId: string;
  eventTitle: string;
  eventDate: Timestamp;
  reservationId: string;
  referredUserId: string;
  referredUserEmail: string;
  referredUserName: string;
  referralCode: string;
  ticketAmount: number;
  originalTicketAmount: number;
  cashbackPercentage: number;
  cashbackAmount: number;
  friendDiscountAmount: number;
  payoutMethods: ('wallet' | 'bank_transfer' | 'instant_payout' | 'percentage_refund')[];
  status: 'clicked' | 'pending_conversion' | 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  eligibleAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface UserReferralStats {
  eventId: string;
  eventTitle: string;
  eventDate: Timestamp;
  sharerId: string;
  sharerName: string;
  sharerEmail: string;
  referralLink: string;
  referralCode: string;
  totalShares: number;
  totalClicks: number;
  totalReferrals: number;
  totalRevenue: number;
  totalCashbackEarned: number;
  pendingCashback: number;
  processingCashback: number;
  paidCashback: number;
  cancelledCashback: number;
  status: 'active' | 'processing' | 'completed' | 'cancelled';
  lastClickAt?: Timestamp;
  lastReferralAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ShareBoostAnalytics {
  eventId: string;

  // Overview stats
  totalSharers: number;
  totalShares: number;
  totalClicks: number;
  clickThroughRate: number;
  totalConversions: number;
  conversionRate: number;
  totalRevenue: number;
  totalCashbackAllocated: number;
  totalCashbackPaid: number;
  budgetRemaining: number;

  // Status distribution
  pendingCount: number;
  pendingAmount: number;
  processingCount: number;
  processingAmount: number;
  completedCount: number;
  completedAmount: number;
  cancelledCount: number;
  cancelledAmount: number;

  // Top performers
  topSharers: TopSharer[];

  // Recent activity
  recentTransactions: CashbackTransaction[];
  recentClicks: ShareClick[];
}

export interface TopSharer {
  sharerId: string;
  sharerName: string;
  sharerEmail: string;
  totalReferrals: number;
  totalRevenue: number;
  totalCashbackEarned: number;
  totalClicks: number;
  conversionRate: number;
  status: string;
}

export interface ShareClick {
  id: string;
  sharerId: string;
  sharerName?: string;
  referralCode: string;
  eventId: string;
  referredUserId?: string;
  referredUserEmail?: string;
  potentialAmount: number;
  clickedAt: Timestamp;
}

// ShareBoost Configuration Interface
export interface ShareBoostConfig {
  enabled: boolean;

  // Basic reward settings
  cashbackPercentage: number; // 5-20%
  friendDiscountPercentage: number; // 0-20%

  // Advanced settings
  minPurchaseAmount: number;
  maxCashbackPerShare: number;
  maxTotalBudget: number;
  processingDelayDays: number;

  // Payout settings
  payoutMethods: ('wallet' | 'bank_transfer' | 'instant_payout' | 'percentage_refund')[];
  minPayoutAmount: number;

  // Eligibility settings
  requireSharerPurchase?: boolean; // If true, sharer must purchase a ticket to receive cashback

  // Tracking and limits
  maxSharesPerUser?: number;
  maxRewardsPerUser?: number;
  expirationDays?: number;

  // Analytics
  totalCashbackPaid?: number;
  totalConversions?: number;
  totalShares?: number;

  // Metadata
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string;
}

export interface ShareBoostConfigRequest {
  eventId: string;
  config: Partial<ShareBoostConfig>;
}

export interface ShareBoostConfigResponse {
  eventId: string;
  config: ShareBoostConfig;
}

// Default ShareBoost configuration
export const DEFAULT_SHAREBOOST_CONFIG: ShareBoostConfig = {
  enabled: false,
  cashbackPercentage: 10,
  friendDiscountPercentage: 5,
  minPurchaseAmount: 20,
  maxCashbackPerShare: 50,
  maxTotalBudget: 1000,
  processingDelayDays: 7,
  payoutMethods: ['percentage_refund'], // Default: sharer gets cashbackPercentage of tickets they helped sell
  minPayoutAmount: 10,
  requireSharerPurchase: false,
  maxSharesPerUser: 100,
  maxRewardsPerUser: 500,
  expirationDays: 30,
  totalCashbackPaid: 0,
  totalConversions: 0,
  totalShares: 0,
};
